import { useState, useEffect } from 'react';
import { fuelCostAPI } from '../../services/api';

const REGIONS = [
	{ code: 'US', name: 'United States', unit: 'gallon' },
	{ code: 'CN', name: 'China', unit: 'liter' },
	{ code: 'VN', name: 'Vietnam', unit: 'liter' },
	{ code: 'IN', name: 'India', unit: 'liter' },
	{ code: 'MX', name: 'Mexico', unit: 'liter' },
	{ code: 'CA', name: 'Canada', unit: 'liter' }
];

export default function FuelCostAdmin() {
	const [prices, setPrices] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [editing, setEditing] = useState(null);
	const [editValue, setEditValue] = useState('');
	const [fetchingEIA, setFetchingEIA] = useState(false);

	useEffect(() => {
		loadPrices();
	}, []);

	const loadPrices = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await fuelCostAPI.getAllCurrent();

			// Convert array to object keyed by region
			const priceMap = {};
			response.data.forEach(price => {
				priceMap[price.region] = price;
			});
			setPrices(priceMap);
		} catch (err) {
			setError('Failed to load fuel prices: ' + (err.message || 'Unknown error'));
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = (region) => {
		const currentPrice = prices[region]?.pricePerUnit || '';
		setEditing(region);
		setEditValue(currentPrice.toString());
	};

	const handleCancel = () => {
		setEditing(null);
		setEditValue('');
	};

	const handleSave = async (region) => {
		try {
			setError(null);
			const regionInfo = REGIONS.find(r => r.code === region);

			await fuelCostAPI.update({
				region,
				fuelType: 'diesel',
				pricePerUnit: parseFloat(editValue),
				unit: regionInfo?.unit || 'gallon',
				source: 'manual_admin'
			});

			setSuccess(`Updated fuel price for ${region}`);
			setEditing(null);
			setEditValue('');
			loadPrices();

			setTimeout(() => setSuccess(null), 3000);
		} catch (err) {
			setError('Failed to update price: ' + (err.message || 'Unknown error'));
		}
	};

	const handleFetchEIA = async () => {
		try {
			setFetchingEIA(true);
			setError(null);
			await fuelCostAPI.fetchEIA();
			setSuccess('Successfully fetched latest US diesel price from EIA');
			loadPrices();
			setTimeout(() => setSuccess(null), 3000);
		} catch (err) {
			const msg = err.response?.data?.error || err.message || 'Unknown error';
			setError('EIA fetch unavailable: ' + msg + '. You can still update prices manually.');
		} finally {
			setFetchingEIA(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleDateString();
	};

	if (loading) {
		return <div className="loading">Loading fuel prices...</div>;
	}

	return (
		<div className="form-section">
			<div className="page-header">
				<h2>Fuel Cost Management</h2>
				<div style={{ display: 'flex', gap: '0.5rem' }}>
					<button
						className="btn btn-secondary"
						onClick={loadPrices}
						disabled={loading}
					>
						Refresh
					</button>
					<button
						className="btn btn-secondary"
						onClick={handleFetchEIA}
						disabled={fetchingEIA}
					>
						{fetchingEIA ? 'Fetching...' : 'Fetch EIA (US)'}
					</button>
				</div>
			</div>

			{error && (
				<div className="alert alert-error">{error}</div>
			)}

			{success && (
				<div className="alert alert-success">{success}</div>
			)}

			<p className="text-muted" style={{ marginBottom: '1rem' }}>
				Current diesel fuel prices by region. Prices are used to calculate fuel surcharges
				for trucking estimates. Click Edit to update a price.
			</p>

			<table className="simple-table">
				<thead>
					<tr>
						<th>Region</th>
						<th style={{ textAlign: 'right' }}>Current Price</th>
						<th style={{ textAlign: 'center' }}>Unit</th>
						<th>Last Updated</th>
						<th>Source</th>
						<th style={{ textAlign: 'center' }}>Actions</th>
					</tr>
				</thead>
				<tbody>
					{REGIONS.map(region => {
						const price = prices[region.code];
						const isEditing = editing === region.code;

						return (
							<tr key={region.code}>
								<td>
									<strong>{region.name}</strong>
									<br />
									<span className="text-muted" style={{ fontSize: '0.75rem' }}>{region.code}</span>
								</td>
								<td style={{ textAlign: 'right' }}>
									{isEditing ? (
										<input
											type="number"
											className="filter-input"
											value={editValue}
											onChange={(e) => setEditValue(e.target.value)}
											style={{ width: '100px' }}
											autoFocus
										/>
									) : (
										<span>{price ? `$${Number(price.pricePerUnit).toFixed(2)}` : '--'}</span>
									)}
								</td>
								<td style={{ textAlign: 'center' }}>
									{region.unit}
								</td>
								<td>
									{formatDate(price?.priceDate)}
								</td>
								<td>
									<span className="text-muted" style={{ fontSize: '0.75rem' }}>
										{price?.source || '--'}
									</span>
								</td>
								<td style={{ textAlign: 'center' }}>
									{isEditing ? (
										<div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
											<button
												className="btn btn-sm btn-primary"
												onClick={() => handleSave(region.code)}
												disabled={!editValue || parseFloat(editValue) <= 0}
											>
												Save
											</button>
											<button
												className="btn btn-sm btn-secondary"
												onClick={handleCancel}
											>
												Cancel
											</button>
										</div>
									) : (
										<button
											className="btn btn-sm btn-secondary"
											onClick={() => handleEdit(region.code)}
										>
											Edit
										</button>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
